import { useState } from 'react';

import { useSessionStorage } from '@/hooks/use-session-storage';

export interface VideoPlayerState {
  isPlaying: boolean;
  currentVolume: number;
  actualWidth: string;
  currentRate: number;
  played: number;
  duration: number;
  isHovered: boolean;
  isControlsHovered: boolean;
  isVideoLoading: boolean;
  videoHasError: boolean;
  isAudio: boolean;
}

export interface VideoPlayerActions {
  setIsPlaying: (value: boolean) => void;
  setCurrentVolume: (value: number) => void;
  setActualWidth: (value: string) => void;
  setCurrentRate: (value: number) => void;
  setPlayed: (value: number) => void;
  setDuration: (value: number) => void;
  setIsHovered: (value: boolean) => void;
  setIsControlsHovered: (value: boolean) => void;
  setIsVideoLoading: (value: boolean) => void;
  setVideoHasError: (value: boolean) => void;
  setIsAudio: (value: boolean) => void;
}

export function useVideoPlayerState(
  initialPlaying: boolean = true,
  initialVolume: number = 1,
  initialWidth: string = '100%',
  initialRate: number = 1
): [VideoPlayerState, VideoPlayerActions] {
  const [isPlaying, setIsPlaying] = useState<boolean>(initialPlaying);
  const [currentVolume, setCurrentVolume] = useSessionStorage<number>(
    'sesion-volume',
    initialVolume
  );
  const [actualWidth, setActualWidth] = useSessionStorage<string>('sesion-width', initialWidth);
  const [currentRate, setCurrentRate] = useState<number>(initialRate);
  const [played, setPlayed] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isControlsHovered, setIsControlsHovered] = useState<boolean>(false);
  const [isVideoLoading, setIsVideoLoading] = useState<boolean>(true);
  const [videoHasError, setVideoHasError] = useState<boolean>(false);
  const [isAudio, setIsAudio] = useState<boolean>(false);

  const state: VideoPlayerState = {
    isPlaying,
    currentVolume,
    actualWidth,
    currentRate,
    played,
    duration,
    isHovered,
    isControlsHovered,
    isVideoLoading,
    videoHasError,
    isAudio,
  };

  const actions: VideoPlayerActions = {
    setIsPlaying,
    setCurrentVolume,
    setActualWidth,
    setCurrentRate,
    setPlayed,
    setDuration,
    setIsHovered,
    setIsControlsHovered,
    setIsVideoLoading,
    setVideoHasError,
    setIsAudio,
  };

  return [state, actions];
}
